// src/pages/profileSetting/index.tsx

import React, { useState, useRef, ChangeEvent, useEffect, useContext } from 'react';
import { HiPencilSquare } from 'react-icons/hi2';
import NoticeComponent from '../../components/NoticeComponent';
import ProfileSidebar from '../../components/ProfileSidebar';
import { NavLink } from 'react-router-dom';
import Modal from '../../components/Modal';
import { instance } from '../../api/axiosInstance'; // Axios 인스턴스 임포트
import { UserDTO } from './UserDTO'; // UserDTO 인터페이스 임포트
import Cookies from 'js-cookie';
import { FaHome, FaUserAlt, FaBookmark, FaGift, FaEnvelope } from 'react-icons/fa';
// react-toastify 추가
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UserContext } from "../../components/UserContext";
import { useTranslation } from 'react-i18next'; // useTranslation 훅 임포트

export default function ProfileSetting() {
  const { t } = useTranslation(); // useTranslation 훅 사용
  const { user, setUser } = useContext(UserContext); // UserContext 사용
  const [email, setEmail] = useState<string>(''); // 현재 이메일
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalEmail, setModalEmail] = useState<string>(''); // 모달에서 편집할 이메일
  const userImageInputRef = useRef<HTMLInputElement | null>(null); // 유저 이미지 업로드용
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const closeDeleteModal = () => setIsDeleteModalOpen(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // 제출 상태
  const [referralCode, setReferralCode] = useState<string>('');

  // 사용자 정보 로드
  useEffect(() => {
    if (user) {
      setEmail(user.email);
    }
  }, [user]);

  // 사용자 프로필 정보 가져오는 함수
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return; // Early return if user is null

      try {
        const response = await instance.get(`/api/user-profiles/${user.id}`);
        if (response.status === 200) {
          setReferralCode(response.data.referrer); // referrer 필드로 설정
        }
      } catch (error) {
        console.error('사용자 프로필 로드 실패:', error);
        toast.error(t('profile_setting.error.fetch_profile'));
      }
    };

    fetchUserProfile();
  }, [user, t]);

  const opendeletemodal = () => {
    setIsDeleteModalOpen(true);
  }

  const handleDeleteAccount = () => {
    if (user) {
      // 쿠키에서 access_token 가져오기
      const accessToken = Cookies.get('access_token');

      if (!accessToken) {
        toast.error(t('profile_setting.error.no_token'));
        return;
      }

      instance.post(`/auth/users/${user.username}/delete`, null, {
        headers: {
          'Authorization': `Bearer ${accessToken}`, // Authorization 헤더 수동 추가
        },
      })
        .then(response => {
          toast.success(response.data.message || t('profile_setting.success.delete_account'));
          setUser(null); // 사용자 상태 초기화
        })
        .catch(error => {
          console.error('계정 삭제 요청 실패:', error);
          const errorMessage = error.response?.data?.message || t('profile_setting.error.delete_account');
          toast.error(errorMessage);
        });
    } else {
      toast.error(t('profile_setting.error.no_user'));
    }
  };

  // 핸들러: 유저 이미지 업로드
  const handleUserImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const accessToken = Cookies.get('access_token');

        if (!accessToken) {
          throw new Error(t('profile_setting.error.no_token'));
        }

        const response = await instance.post('/auth/users/profile-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        if (response.status === 200) {
          const updatedUser: UserDTO = response.data;
          setUser(updatedUser);
          toast.success(t('profile_setting.success.upload_image'));
        }
      } catch (error: any) {
        console.error('프로필 이미지 업로드 실패:', error);
        let errorMessage = t('profile_setting.error.upload_image');
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        toast.error(errorMessage);
      }
    }
  };

  // 핸들러: 유저 이미지 클릭 시 파일 선택기 열기
  const handleUserImageClick = () => {
    if (userImageInputRef.current) {
      userImageInputRef.current.click();
    }
  };

  // 핸들러: 모달 열기
  const openModal = () => {
    setModalEmail(email);
    setIsModalOpen(true);
  };

  // 핸들러: 모달 닫기
  const closeModal = () => setIsModalOpen(false);

  // 핸들러: 모달에서 이메일 변경
  const handleModalEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setModalEmail(event.target.value);
  };

  // 핸들러: 이메일 저장
  const handleSaveEmail = async () => {
    if (user) {
      setIsSubmitting(true);
      try {
        const response = await instance.post('/auth/users/change-email', { email: modalEmail });

        if (response.status === 200) {
          const updatedUser: UserDTO = response.data;
          setUser(updatedUser);
          setEmail(updatedUser.email);
          closeModal();
          toast.success(t('profile_setting.success.change_email'));
        }
      } catch (error: any) {
        console.error('이메일 변경 실패:', error);
        const errorMessage = error.response?.data?.message || t('profile_setting.error.change_email');
        toast.error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      toast.error(t('profile_setting.error.no_user'));
    }
  };

  return (
    <section className="max-w-[1440px] w-full h-screen mx-auto mt-[90px]">
      <div className="flex flex-col flex-1 w-full">
        <div className="flex">
          {/* 사이드바 */}
          <div className="mobile:hidden block min-h-[500px] h-full">
            <ProfileSidebar />
          </div>
          {/* 프로필 콘텐츠 */}
          <article className="h-full w-full">
            <div className="w-full h-full flex flex-row items-start justify-center">
              <div className="max-w-[1440px] w-full pt-[48px] pb-12">
                <div className="ml-[80px] mobile:ml-0 mobile:px-4 ">
                  <NoticeComponent
                    title={t('profile_setting.section_title')}
                    content={t('profile_setting.section_content')}
                  />
                </div>
                <div className="flex flex-col justify-center items-stretch w-full px-[80px] mobile:px-4">
                  {/* 배너 * 이름 * 유저네임 * 내 정보 */}
                  <div>
                    <section className="w-full">
                      <div className="w-[350px] flex flex-col">
                        <label
                          htmlFor="uuid"
                          className="text-sm font-bold text-MainTextColor"
                        >
                          {t('profile_setting.account_info')}
                        </label>
                      </div>

                      <div className="flex mt-4">
                        {/* 계정 아이디 */}
                        <div className="w-[380px] flex flex-col relative">
                          <label
                            htmlFor="account-id"
                            className="absolute left-2 top-2 text-[10px] font-extralight text-[#a8a8a8]"
                          >
                            {t('profile_setting.account_id')}
                          </label>
                          <input
                            id="account-id"
                            type="text"
                            value={user?.username || ''}
                            readOnly
                            className="w-full pb-4 pt-8 pl-2 h-[53px] rounded-md bg-gray-100 text-MainTextColor border border-gray-200 focus:outline-none"
                            placeholder={t(
                              'profile_setting.account_id_placeholder'
                            )}
                          />
                        </div>
                        {/* 이메일 */}
                        <div className="w-[380px] flex flex-col ml-[30px] relative">
                          <label
                            htmlFor="email"
                            className="absolute left-2 top-2 text-[10px] font-extralight text-[#a8a8a8]"
                          >
                            {t('profile_setting.email_address')}
                          </label>
                          <input
                            id="email"
                            type="text"
                            value={email}
                            readOnly
                            className="w-full pb-4 pt-8 pl-2 h-[53px] rounded-md bg-gray-100 text-MainTextColor border border-gray-200 focus:outline-none"
                            placeholder={t('profile_setting.email_placeholder')}
                          />
                        </div>
                      </div>
                    </section>

                    {/* 프로필 이미지 */}
                    <div className="mt-[34px]">
                      <label
                        htmlFor="profile-image"
                        className="text-sm font-bold text-MainTextColor mt-4"
                      >
                        {t('profile_setting.profile_image')}
                      </label>
                      <p className="text-sm text-[#c4c4c4] font-normal mt-2">
                        {t('profile_setting.upload_image_instruction')}
                      </p>
                      <div
                        className="w-[182px] h-[182px] relative border border-gray-200 rounded-lg mt-4 cursor-pointer"
                        onClick={handleUserImageClick}
                      >
                        {user?.profileImageUrl ? (
                          <img
                            src={`${instance.defaults.baseURL}${
                              user.profileImageUrl
                            }?t=${new Date().getTime()}`} // 캐싱 방지
                            alt={t('profile_setting.user_image')}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : user?.fullName ? (
                          <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
                            <span className="text-xl font-bold text-gray-400">
                              {user.fullName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
                            <span className="text-sm text-gray-400">
                              {t('profile_setting.upload_image')}
                            </span>
                          </div>
                        )}
                        <input
                          type="file"
                          ref={userImageInputRef}
                          className="hidden"
                          onChange={handleUserImageUpload}
                          accept="image/*"
                        />
                      </div>
                    </div>

                    {/* 추천 코드 표시 */}
                    <div className="mt-[34px]">
                      <h3 className="text-lg font-bold text-MainTextColor">
                        {t('profile_setting.referral_code')}
                      </h3>
                      <p className="text-md text-gray-600 mt-2">
                        {referralCode || t('profile_setting.no_referral_code')}
                      </p>
                    </div>

                    <hr className="border border-dashed h-[2px] my-[48px]" />
                  </div>

                  {/* 계정 삭제 */}
                  <div className="pr-[80px]">
                    <h2 className="text-xl font-bold mb-4">
                      {t('profile_setting.delete_account')}
                    </h2>
                    <p className="text-sm font-light leading-relaxed mb-4 text-[#707070]">
                      {t('profile_setting.delete_account_info_1')}
                    </p>
                    <p className="text-sm font-light leading-relaxed mb-4 text-[#707070]">
                      {t('profile_setting.delete_account_info_2')}
                    </p>
                    <button
                      onClick={opendeletemodal}
                      className="px-4 py-3 text-xs font-medium tracking-wider text-white shadow-md transition duration-300 ease-in-out bg-red-600 rounded hover:bg-red-500 focus:outline-none"
                    >
                      {t('profile_setting.request_delete')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
      {/* 모달 */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        width="500px"
        height="370px"
      >
        <h2 className="text-xl mb-4 font-bold text-MainTextColor tracking-normal">
          {t('profile_setting.add_new_email')}
        </h2>
        <p className="mb-4 text-[13px] text-[#272727]">
          {t('profile_setting.email_change_info')}
        </p>
        <form>
          <div className="relative mt-6">
            <input
              type="text"
              id="modal-email"
              className="peer w-full px-4 py-5 h-[65px] border text-MainTextColor rounded-lg placeholder-transparent focus:outline-none focus:border-black"
              placeholder={t('profile_setting.new_email_placeholder')}
              value={modalEmail}
              onChange={handleModalEmailChange}
            />

            <label
              htmlFor="modal-email"
              className="absolute left-3 text-gray-600 transition-all peer-placeholder-shown:top-[22px] peer-placeholder-shown:text-[15px] peer-focus:-top-[-6px] peer-focus:text-xs"
            >
              {t('profile_setting.new_email_label')}
            </label>
          </div>

          <button
            type="button"
            className="w-full py-4 mt-6 bg-black text-white font-bold text-sm rounded-lg hover:bg-opacity-70 transition duration-300"
            onClick={handleSaveEmail}
            disabled={isSubmitting} // 제출 중일 때 버튼 비활성화
          >
            {isSubmitting
              ? t('profile_setting.save_loading')
              : t('profile_setting.save_changes')}
          </button>
          {/* 취소 버튼 */}
          <button
            type="button"
            className="w-full py-4 mt-2 bg-gray-300 text-black font-bold text-sm rounded-lg hover:bg-opacity-70 transition duration-300"
            onClick={closeModal}
          >
            {t('profile_setting.cancel')}
          </button>
        </form>
      </Modal>
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        width="400px"
        height="200px"
      >
        <h1 className="font-bold text-MainTextColor text-center text-3xl mt-4">
          {t('profile_setting.delete_account_title')}
        </h1>
        <h2 className="text-lg mb-4 font-medium mt-2 text-center text-[#555] tracking-normal">
          {t('profile_setting.confirm_delete_account')}
        </h2>
        <div className="flex flex-col font-semibold text-base tracking-wide gap-2 mt-8">
          <button
            onClick={handleDeleteAccount}
            className="py-3 px-4 bg-MainColor text-white rounded-lg hover:bg-red-700"
          >
            {t('profile_setting.delete')}
          </button>
          <button
            onClick={closeDeleteModal}
            className="py-3 px-4 bg-gray-300 text-black rounded-lg hover:bg-gray-400"
          >
            {t('profile_setting.cancel')}
          </button>
        </div>
      </Modal>

      {/* 모바일 하단 네비게이션 바 */}
      <nav className="md:hidden fixed bottom-0 w-full bg-wi text-white border-t border-gray-700 flex justify-around items-center py-2">
        <NavLink
          to="/setting/setting"
          className={({ isActive }) =>
            `flex flex-col items-center text-xs ${
              isActive ? 'text-white' : 'text-gray-400'
            } hover:text-white`
          }
        >
          <FaUserAlt className="w-6 h-6 mb-1" />
          내 정보
        </NavLink>
        <NavLink
          to="/setting/account"
          className={({ isActive }) =>
            `flex flex-col items-center text-xs ${
              isActive ? 'text-white' : 'text-gray-400'
            } hover:text-white`
          }
        >
          <FaUserAlt className="w-6 h-6 mb-1" />
          계정
        </NavLink>
        <NavLink
          to="/setting/sns"
          className={({ isActive }) =>
            `flex flex-col items-center text-xs ${
              isActive ? 'text-white' : 'text-gray-400'
            } hover:text-white`
          }
        >
          <FaBookmark className="w-6 h-6 mb-1" />
          SNS 주소
        </NavLink>
        <NavLink
          to="/setting/coupon"
          className={({ isActive }) =>
            `flex flex-col items-center text-xs ${
              isActive ? 'text-white' : 'text-gray-400'
            } hover:text-white`
          }
        >
          <FaGift className="w-6 h-6 mb-1" />
          쿠폰
        </NavLink>
        <NavLink
          to="/setting/newslatter"
          className={({ isActive }) =>
            `flex flex-col items-center text-xs ${
              isActive ? 'text-white' : 'text-gray-400'
            } hover:text-white`
          }
        >
          <FaEnvelope className="w-6 h-6 mb-1" />
          뉴스레터
        </NavLink>
      </nav>
    </section>
  );
}
